// src/components/admin/MemberEditModal.js
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import api from '../../utils/api';
import { showToast } from '../../utils/toast';

/**
 * Modal component for editing member roles and permissions
 * 
 * @param {Object} member - The member object to edit
 * @param {boolean} isOpen - Whether the modal is open
 * @param {Function} onClose - Function to call when the modal is closed
 * @param {Function} onMemberUpdated - Function to call when the member is updated
 * @returns {JSX.Element} Rendered component
 */
const MemberEditModal = ({ member, isOpen, onClose, onMemberUpdated }) => {
  const [formData, setFormData] = useState({
    role: 'employee',
    permissions: []
  });
  const [saving, setSaving] = useState(false);
  
  // Available permissions (these should match the backend enum)
  const availablePermissions = [
    { id: 'manage_users', label: 'Manage Users' },
    { id: 'manage_perks', label: 'Manage Perks' },
    { id: 'manage_settings', label: 'Manage Settings' },
    { id: 'view_analytics', label: 'View Analytics' },
    { id: 'approve_perks', label: 'Approve Perks' },
    { id: 'approve_trades', label: 'Approve Trades' },
    { id: 'invite_users', label: 'Invite Users' }
  ];
  
  // Initialize form data when member changes
  useEffect(() => {
    if (member) {
      setFormData({
        role: member.role || 'employee',
        permissions: member.permissions || []
      });
    }
  }, [member]);
  
  // Handle role change
  const handleRoleChange = (e) => {
    const role = e.target.value;
    
    // If role is admin, include all permissions
    if (role === 'admin') {
      setFormData({
        role,
        permissions: availablePermissions.map(permission => permission.id)
      });
    } else {
      setFormData({
        role,
        permissions: []
      });
    }
  };
  
  // Handle permission toggle
  const handlePermissionChange = (e) => {
    const permission = e.target.value;
    const isChecked = e.target.checked;
    
    setFormData(prev => {
      if (isChecked) {
        return {
          ...prev,
          permissions: [...prev.permissions, permission]
        };
      } else {
        return {
          ...prev,
          permissions: prev.permissions.filter(p => p !== permission)
        };
      }
    });
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const response = await api.put(`/organizations/members/${member._id}`, formData);
      showToast.success('Member updated successfully');
      
      if (onMemberUpdated && typeof onMemberUpdated === 'function') {
        onMemberUpdated(response.data);
      }
      
      onClose();
    } catch (error) {
      console.error('Error updating member:', error);
      showToast.error(error.response?.data?.message || 'Failed to update member');
    } finally {
      setSaving(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-x-hidden overflow-y-auto bg-black bg-opacity-50">
      <div className="relative w-full max-w-md mx-auto my-6">
        <div className="relative flex flex-col w-full bg-white border-0 rounded-lg shadow-lg">
          <div className="flex items-center justify-between p-5 border-b border-gray-200 rounded-t">
            <h3 className="text-lg font-semibold text-gray-900">
              Edit Member: {member?.name}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <span className="sr-only">Close</span>
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="p-6 space-y-4">
              <div>
                <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                  Role
                </label>
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleRoleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="employee">Employee</option>
                  <option value="admin">Admin</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Admins have full access to all features. Employees can have specific permissions.
                </p>
              </div>
              
              {formData.role === 'employee' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Permissions
                  </label>
                  <div className="space-y-2">
                    {availablePermissions.map(permission => (
                      <div key={permission.id} className="flex items-center">
                        <input
                          id={`permission-${permission.id}`}
                          type="checkbox"
                          value={permission.id}
                          checked={formData.permissions.includes(permission.id)}
                          onChange={handlePermissionChange}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <label htmlFor={`permission-${permission.id}`} className="ml-2 block text-sm text-gray-700">
                          {permission.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex items-center justify-end p-6 border-t border-gray-200 rounded-b">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="ml-3 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

MemberEditModal.propTypes = {
  member: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    username: PropTypes.string,
    email: PropTypes.string.isRequired,
    role: PropTypes.string.isRequired,
    permissions: PropTypes.arrayOf(PropTypes.string)
  }),
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onMemberUpdated: PropTypes.func
};

export default MemberEditModal;